/**
 * apps/web :: components/paywall/UpgradeCallout
 *
 * Shows a "you have X days left on your tourist pass; upgrade now and
 * we'll credit $Y" callout (FE-04, ROADS-445). Lives above the paywall
 * tier grid + above the /checkout form so the user sees the credit
 * offer wherever they're about to pay.
 *
 * Pure presentation. Owners (Paywall, CheckoutPage) compute the source
 * tourist entitlement + days remaining + credit estimate and pass them
 * in. The component renders nothing when the input is null so callers
 * can drop it in unconditionally.
 *
 * a11y:
 *   - role="region" with aria-labelledby so screen readers announce
 *     this as its own landmark.
 *   - The inline numbers (days, credit) are wrapped in <strong> for
 *     visual emphasis, NOT for screen-reader pause — the surrounding
 *     ICU plural string carries the semantic meaning.
 */
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";

export interface UpgradeCalloutProps {
  /** How many days are still left on the tourist entitlement. */
  daysRemaining: number;
  /**
   * Approximate USD credit that will roll forward. Computed client-side
   * as `(daysRemaining / 180) * 15` rounded to 2 decimals; the backend
   * is the source of truth (response field `credit_applied_cents`) but
   * this is a fine pre-checkout estimate.
   */
  creditUsd: number;
  /** Optional handler for the inline "Learn more" / "Apply credit" CTA. */
  onUpgradeClick?: () => void;
  /**
   * When true, render an action button as part of the callout. Off by
   * default — the paywall tier grid already has per-tier CTAs, so we
   * only show a CTA on the standalone /checkout success/cancel path.
   */
  showCta?: boolean;
}

export default function UpgradeCallout({
  daysRemaining,
  creditUsd,
  onUpgradeClick,
  showCta = false,
}: UpgradeCalloutProps): JSX.Element | null {
  const { t } = useTranslation();
  if (daysRemaining <= 0 || creditUsd <= 0) return null;

  const headingId = "upgrade-callout-heading";
  return (
    <section
      role="region"
      aria-labelledby={headingId}
      data-testid="upgrade-callout"
      className="mb-md flex flex-col gap-sm rounded-2xl border border-amber-200 bg-amber-50 p-md text-amber-900"
    >
      <div className="flex items-center gap-sm">
        <Info aria-hidden="true" size={18} className="text-amber-600" />
        <span className="text-xs font-semibold uppercase tracking-wide">
          {t("paywall.upgrade.eyebrow", { defaultValue: "Tourist credit available" })}
        </span>
      </div>
      <h2 id={headingId} className="text-base font-semibold">
        {t("paywall.upgrade.headline", {
          defaultValue: "Upgrade now and we'll credit your remaining tourist days",
        })}
      </h2>
      <p className="text-sm">
        {t("paywall.upgrade.body", {
          count: daysRemaining,
          days: daysRemaining,
          credit: creditUsd.toFixed(2),
          defaultValue:
            "You have {{days}} day(s) left on your Tourist pass. " +
            "Upgrade to Class C now and we'll credit ${{credit}} toward your first 6 months.",
        })}
      </p>
      {showCta && onUpgradeClick ? (
        <div>
          <button
            type="button"
            data-testid="upgrade-callout-cta"
            onClick={onUpgradeClick}
            className="inline-flex h-11 min-w-[44px] items-center justify-center rounded-xl bg-amber-600 px-md text-sm font-semibold text-white hover:bg-amber-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-700"
          >
            {t("paywall.upgrade.cta", { defaultValue: "Upgrade with credit" })}
          </button>
        </div>
      ) : null}
    </section>
  );
}

/** Days-remaining helper: floor of (expires_at - now) in days. */
export function daysRemainingFor(
  expiresAtMs: number,
  nowMs: number,
): number {
  const ms = expiresAtMs - nowMs;
  if (ms <= 0) return 0;
  return Math.floor(ms / (24 * 60 * 60 * 1_000));
}

/** Credit estimate ($15 / 180 days). Mirrors the backend service math. */
export function creditEstimateUsd(daysRemaining: number): number {
  if (daysRemaining <= 0) return 0;
  const cents = Math.round((daysRemaining / 180) * 1500);
  return cents / 100;
}
